import { useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { landingPathFor } from "@/lib/landingPath";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Sparkle, Buildings, Heart, ArrowRight, Question } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login } = useAuth();
  const { brand } = useBranding();
  const navigate = useNavigate();
  const { slug: urlSlug } = useParams();  // /t/:slug/login
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || null;
  const deactivatedMsg = searchParams.get("deactivated") ? (searchParams.get("msg") || "Your application access has been deactivated.") : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [workspacePick, setWorkspacePick] = useState(null); // list of {slug,name} when the user needs to pick

  // Persist last-used slug so returning users don't have to remember it.
  const persistedSlug = typeof window !== "undefined" ? localStorage.getItem("tf_org_slug") : null;
  const slug = urlSlug || persistedSlug || null;

  const brandInitial = (brand.brand_name || "T").trim().charAt(0).toUpperCase() || "T";

  const attemptLogin = async (pickedSlug) => {
    setLoading(true);
    try {
      const result = await login(email, password, pickedSlug || null);
      if (result?.needs_workspace_selection) {
        // User is a member of multiple workspaces — surface the picker.
        setWorkspacePick(result.orgs || []);
        return;
      }
      setWorkspacePick(null);
      toast.success("Welcome back");
      // If the user was bounced here from another route (e.g. QR check-in
      // deep link), respect the `next` param — but only same-origin paths.
      if (nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//")) {
        navigate(nextPath);
      } else {
        navigate(landingPathFor(result));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    await attemptLogin(slug);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Hero */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden bg-primary text-primary-foreground">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/33812025/pexels-photo-33812025.jpeg')",
            backgroundSize: "cover", backgroundPosition: "center",
            mixBlendMode: "luminosity",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/85 to-primary/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-lg bg-primary-foreground text-primary flex items-center justify-center font-display font-bold text-xl overflow-hidden">
                {(brand.effective_logo_url || brand.logo_url) ? (
                  <img src={brand.effective_logo_url || brand.logo_url} alt={brand.brand_name} className="h-11 w-11 object-contain" />
                ) : brandInitial}
              </div>
              <div>
                <div className="font-display font-bold text-2xl tracking-tight">{brand.brand_name}</div>
                <div className="overline text-primary-foreground/70">Therapy Center ERP</div>
              </div>
            </div>
          </div>

          <div className="max-w-md space-y-6">
            <h1 className="font-display text-5xl font-bold tracking-tight leading-[1.05]">
              {brand.login_tagline}
            </h1>
            <p className="text-primary-foreground/85 text-base leading-relaxed">
              From student check-in and queue assignment to live sessions, assessments, invoices and payments — all in one calm, purposeful workspace.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4">
              {[
                { Icon: Buildings, label: "Multi-branch" },
                { Icon: Sparkle, label: "One login, many roles" },
                { Icon: Heart, label: "Made for care" },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex flex-col gap-2 items-start">
                  <Icon size={22} weight="duotone" />
                  <div className="text-xs text-primary-foreground/85">{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-primary-foreground/60 tracking-wide">
            &copy; {brand.brand_name} · {brand.footer_text}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-sm space-y-6">
          <div className="lg:hidden flex items-center gap-2.5 mb-4">
            <div className="h-10 w-10 rounded-md bg-primary flex items-center justify-center font-display font-bold text-primary-foreground text-lg overflow-hidden">
              {(brand.effective_logo_url || brand.logo_url) ? (
                <img src={brand.effective_logo_url || brand.logo_url} alt={brand.brand_name} className="h-10 w-10 object-contain" />
              ) : brandInitial}
            </div>
            <div className="font-display font-bold text-xl">{brand.brand_name}</div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="overline">Sign in</div>
              {slug && (
                <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary text-[10px] font-medium" data-testid="workspace-badge">
                  <Buildings size={10} weight="duotone" className="mr-1" /> {slug}
                </Badge>
              )}
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Welcome back.</h2>
            <p className="text-muted-foreground text-sm">
              {slug
                ? <>Sign in to the <span className="font-medium text-foreground">{slug}</span> workspace.</>
                : "Continue with Google or use your email."}
            </p>
          </div>

          {deactivatedMsg && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3 mb-4"
              data-testid="deactivated-notice"
            >
              {deactivatedMsg}
            </div>
          )}

          {/* Google — primary CTA */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 gap-2.5 font-medium border-border hover:bg-muted/60"
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center">
              <span className="bg-background px-3 text-[11px] uppercase tracking-widest text-muted-foreground">Or with email</span>
            </div>
          </div>

          {/* Email + password */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 gap-2" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight size={16} />
            </Button>
          </form>

          {/* Workspace picker — surfaces when the email is a member of 2+ orgs */}
          {workspacePick && workspacePick.length > 0 && (
            <Card className="border-primary/40 bg-primary/5" data-testid="workspace-picker">
              <CardContent className="p-4 space-y-2">
                <div className="text-sm font-semibold">Pick a workspace to sign into</div>
                <p className="text-[11px] text-muted-foreground">
                  This email belongs to {workspacePick.length} workspaces. Choose one below — you can only be signed into a single workspace at a time.
                </p>
                <div className="grid gap-1.5 pt-1">
                  {workspacePick.map(o => (
                    <button
                      key={o.slug} type="button"
                      onClick={() => attemptLogin(o.slug)}
                      disabled={loading}
                      data-testid={`pick-workspace-${o.slug}`}
                      className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2 hover:bg-muted transition text-left"
                    >
                      <span>
                        <span className="font-medium text-sm">{o.name}</span>
                        <span className="block text-[10px] text-muted-foreground">/t/{o.slug}</span>
                      </span>
                      <ArrowRight size={14} className="text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            New Google sign-ins are provisioned as <span className="font-medium text-foreground">guardians</span> — ask your admin to upgrade your role if you&apos;re staff.
          </p>

          <div className="flex items-center justify-between text-xs pt-1">
            <FindWorkspaceLink />
            <Link
              to="/signup"
              data-testid="new-workspace-link"
              className="text-primary hover:underline font-medium"
            >
              Create a workspace →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}


function FindWorkspaceLink() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [orgs, setOrgs] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/auth/orgs-for-email?email=${encodeURIComponent(email.trim())}`);
      setOrgs(data.orgs || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setOrgs(null); setEmail(""); } }}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="find-workspace-link"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <Question size={12} /> Forgot your workspace?
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-card">
        <DialogHeader><DialogTitle>Find your workspaces</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Email</Label>
          <div className="flex gap-2">
            <Input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="find-workspace-email"
            />
            <Button onClick={lookup} disabled={loading} data-testid="find-workspace-lookup">
              {loading ? "…" : "Look up"}
            </Button>
          </div>
          {orgs !== null && (
            <div className="pt-2 space-y-1.5">
              {orgs.length === 0 ? (
                <p className="text-xs text-muted-foreground">No active workspaces found for that email.</p>
              ) : (
                orgs.map(o => (
                  <Link
                    key={o.slug}
                    to={`/t/${o.slug}/login`}
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-2 hover:bg-muted transition"
                    data-testid={`found-workspace-${o.slug}`}
                  >
                    <span>
                      <span className="font-medium text-sm">{o.name}</span>
                      <span className="block text-[10px] text-muted-foreground">/t/{o.slug}</span>
                    </span>
                    <ArrowRight size={14} className="text-muted-foreground" />
                  </Link>
                ))
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
